import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AppThemeProvider } from '@/contexts/AppThemeContext';
import { AppSettingsProvider } from '@/contexts/AppSettingsContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AppShell } from '@/components/layout';
import {
  Dashboard,
  QRList,
  QRDetail,
  QRWizard,
  VisualEditor,
  Analytics,
  Templates,
  Admin,
  Settings,
  AppSettings,
  Login,
  NotFound,
  ShortURLs,
  CreateShortURL,
  URLDetail,
  URLAnalytics,
  LandingPages,
  LandingPageDetails,
  PageBuilder,
  PageTemplates,
  CreatePage,
  BrandKit,
  Collateral,
  CollateralTemplates,
  CollateralEditor,
  Submissions,
  BioLinks,
  BioEditor,
  CreateBio,
  PublicLandingPage,
  PublicBioPage,
  // Marketing Suite
  Campaigns,
  AudienceExtracts,
  CampaignDetail,
  Leads,
  LeadDetail,
  Media,
  UTMBuilder,
  Reports,
  Webhooks,
  Conversions,
  Experiments,
  Workflows,
  UserManagement,
  UserActivity,
  AccessPending,
  AcceptInvite,
  APIKeys,
  AdminErrorLogs,
  // Help centre
  HelpIndex,
  HelpArticle,
} from '@/pages';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import { hasPermission, AUDIENCES_EXPORT } from '@/lib/permissions';
import { isPartner, partnerMayOpen } from '@/lib/partner';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Access gate: authenticated via Alchemy but not yet granted access.
  // Pending/denied (and any non-active) users see the request-access screen
  // instead of the app, regardless of which protected route they hit.
  if (user.status && user.status !== 'active') {
    return <AccessPending />;
  }

  // External partners only get the screens their API allowlist serves
  // (no users, API keys or app settings); anything else goes home.
  if (isPartner(user) && !partnerMayOpen(location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Admin-only gate (nested inside ProtectedRoute, so `user` is present).
// Non-admins are bounced to the dashboard. Used for the collateral feature,
// which is admin-only for now.
function AdminRoute() {
  const { user } = useAuth();
  if (user && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

// Permission gate for pages shown only to the named users an admin granted
// a permission to (admins hold it implicitly). Everyone else sees Not Found,
// as the API answers them 404: the page's existence is not advertised.
function PermissionRoute({ permission }) {
  const { user } = useAuth();
  if (!hasPermission(user, permission)) {
    return <NotFound />;
  }
  return <Outlet />;
}

// Public route wrapper (redirect to dashboard if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public landing page and bio link routes - no auth required */}
      <Route path="/p/:slug" element={<PublicLandingPage />} />
      <Route path="/p/:slug/preview" element={<PublicLandingPage preview />} />
      {/* Locale-specific URL, e.g. /p/dubai-roadshow/fr — path-based so it
          carries hreflang / SEO. "preview" is a reserved word here, handled
          by the route above. */}
      <Route path="/p/:slug/:locale" element={<PublicLandingPage />} />
      <Route path="/b/:slug" element={<PublicBioPage />} />
      <Route path="/b/:slug/preview" element={<PublicBioPage preview />} />
      
      {/* Public routes */}
      <Route
        path="/accept-invite"
        element={<AcceptInvite />}
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected routes with AppShell */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        {/* Redirect root to dashboard */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        {/* Alchemy SSO returns to its registered RedirectUri (/home); LinkHub
            has no /home page, so send it to the dashboard. */}
        <Route path="/home" element={<Navigate to="/dashboard" replace />} />

        {/* Main pages */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/qrs" element={<QRList />} />
        <Route path="/qrs/new" element={<QRWizard />} />
        <Route path="/qrs/:id" element={<QRDetail />} />
        <Route path="/editor" element={<VisualEditor />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/analytics/:qrId" element={<Analytics />} />
        
        {/* URL Shortener pages */}
        <Route path="/urls" element={<ShortURLs />} />
        <Route path="/urls/new" element={<CreateShortURL />} />
        <Route path="/urls/analytics/:id" element={<URLAnalytics />} />
        <Route path="/urls/:id" element={<URLDetail />} />
        
        {/* Landing Pages */}
        <Route path="/pages" element={<LandingPages />} />
        <Route path="/pages/new" element={<CreatePage />} />
        <Route path="/pages/details/:id" element={<LandingPageDetails />} />
        <Route path="/pages/:id" element={<PageBuilder />} />
        <Route path="/page-templates" element={<PageTemplates />} />
        <Route path="/page-templates/:id" element={<PageTemplates />} />
        <Route path="/brand" element={<BrandKit />} />
        {/* Collateral — admin-only for now */}
        <Route element={<AdminRoute />}>
          <Route path="/collateral" element={<Collateral />} />
          <Route path="/collateral/templates" element={<CollateralTemplates />} />
          <Route path="/collateral/templates/new" element={<CollateralEditor />} />
          <Route path="/collateral/templates/:id/edit" element={<CollateralEditor />} />
        </Route>
        <Route path="/submissions" element={<Submissions />} />
        
        {/* Bio Links */}
        <Route path="/bio" element={<BioLinks />} />
        <Route path="/bio/new" element={<CreateBio />} />
        <Route path="/bio/:id" element={<BioEditor />} />
        
        {/* Marketing Suite */}
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/campaigns/:id" element={<CampaignDetail />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/:id" element={<LeadDetail />} />
        <Route path="/media" element={<Media />} />
        <Route path="/utm-builder" element={<UTMBuilder />} />
        <Route element={<PermissionRoute permission={AUDIENCES_EXPORT} />}>
          <Route path="/audience-extracts" element={<AudienceExtracts />} />
        </Route>
        <Route path="/reports" element={<Reports />} />
        <Route path="/webhooks" element={<Webhooks />} />
        <Route path="/conversions" element={<Conversions />} />
        <Route path="/experiments" element={<Experiments />} />
        <Route path="/workflows" element={<Workflows />} />
        
        <Route path="/templates" element={<Templates />} />
        <Route path="/templates/:id" element={<Templates />} />

        {/* Help centre — in-app knowledge base */}
        <Route path="/help" element={<HelpIndex />} />
        <Route path="/help/:slug" element={<HelpArticle />} />

        <Route path="/admin/error-logs" element={<AdminErrorLogs />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/admin/users/:id/activity" element={<UserActivity />} />
        <Route path="/api-keys" element={<APIKeys />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/*" element={<Settings />} />
        <Route path="/app-settings" element={<AppSettings />} />
        
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Catch-all 404 for non-protected routes */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppThemeProvider>
        <AppSettingsProvider>
          <AuthProvider>
            {/*
              Single TooltipProvider for the whole app — needed by the
              <FieldHelp /> component (and any other Tooltip usage).
              delayDuration=200 makes the help tooltips feel responsive
              without firing on incidental hover.
            */}
            <TooltipProvider delayDuration={200}>
              <BrowserRouter>
                <AppRoutes />
                <Toaster position="bottom-right" richColors closeButton />
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </AppSettingsProvider>
      </AppThemeProvider>
    </ThemeProvider>
  );
}

export default App;
